import { Suspense, lazy } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";
// layouts
import MainLayout from "../layouts/main";
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// guards
import GuestGuard from "../guards/GuestGuard";
import AuthGuard from "../guards/AuthGuard";
// config
import { PATH_AFTER_LOGIN } from "../config";
// components
import LoadingScreen from "../components/LoadingScreen";

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense
      fallback={<LoadingScreen isDashboard={pathname.includes("/dashboard")} />}
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: "reset-password", element: <ResetPassword /> },
        { path: "verify", element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: "home", element: <GeneralApp /> },
        { path: "send-topup", element: <SendTopUp /> },
        { path: "send-moncash", element: <SendMoncash /> },
        { path: "send-natcash", element: <SendNatcash /> },
        { path: "money-transfer", element: <SendMoney /> },
        // {
        //   path: "configuration-transaction",
        //   children: [
        //     {
        //       element: (
        //         <Navigate
        //           to="/dashboard/configuration-transaction/user-commission"
        //           replace
        //         />
        //       ),
        //       index: true,
        //     },
        //     { path: "user-commission", element: <UserCommissionList /> },
        //     { path: "exchange-rate", element: <ExchangeRateList /> },
        //     { path: "fee", element: <FeeList /> },
        //   ],
        // },
        // {
        //   path: "security-and-limit",
        //   children: [
        //     {
        //       element: (
        //         <Navigate
        //           to="/dashboard/security-and-limit/limit-transaction-generale"
        //           replace
        //         />
        //       ),
        //       index: true,
        //     },
        //     {
        //       path: "limit-transaction-generale",
        //       element: <LimitTransactionGeneraleList />,
        //     },
        //   ],
        // },
        // agents
        // {
        //   path: "agent",
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/agent/list" replace />,
        //       index: true,
        //     },
        //     { path: "list", element: <AgentList /> },
        //     { path: "new", element: <AgentCreate /> },
        //     { path: ":agentKey/edit", element: <AgentCreate /> },
        //   ],
        // },
        {
          path: "ticket",
          children: [
            {
              element: <Navigate to="/dashboard/ticket/sale" replace />,
              index: true,
            },
            { path: "sale", element: <Tickets /> },
            { path: "cancel", element: <TicketRefund /> },
            { path: "sale/:id", element: <TicketDetails /> },
            { path: "sale/:id/detail", element: <TicketTransacDetails /> },
          ],
        },
        // reports
        {
          path: "report",
          children: [
            {
              element: <Navigate to="/dashboard/report/sale" replace />,
              index: true,
            },
            { path: "sale", element: <Report /> },
            { path: "transactions", element: <TransactionList /> },
          ],
        },
        {
          path: "user",
          children: [
            {
              element: <Navigate to="/dashboard/user/account" replace />,
              index: true,
            },
            { path: "account", element: <UserAccount /> },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        // { path: "politique-confidentialite", element: <PolitiqueConf /> },
        // { path: "termes-conditions", element: <TermesConditions /> },
        // {
        //   path: "politique-securite",
        //   element: <PolitiqueSecurite />,
        // },
        // { path: "coming-soon", element: <ComingSoon /> },
        // { path: "maintenance", element: <Maintenance /> },
        // { path: "500", element: <Page500 /> },
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: "/",
      element: <MainLayout />,
      children: [
        {
          element: <Navigate to="/dashboard" replace />,
          index: true,
        },
        // {
        //   element: (
        //     <GuestGuard>
        //       <HomePage />
        //     </GuestGuard>
        //   ),
        //   index: true,
        // },
        // { path: "about-us", element: <About /> },
        // { path: "contact-us", element: <Contact /> },
        // { path: "faqs", element: <Faqs /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import("../pages/auth/Login")));
const ResetPassword = Loadable(
  lazy(() => import("../pages/auth/ResetPassword"))
);
const VerifyCode = Loadable(lazy(() => import("../pages/auth/VerifyCode")));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(
  lazy(() => import("../pages/dashboard/GeneralApp"))
);

// TICKET SALE
const Tickets = Loadable(lazy(() => import("../pages/dashboard/Tickets")));

const TicketDetails = Loadable(
  lazy(() => import("../pages/dashboard/TicketDetails"))
);

const TicketRefund = Loadable(
  lazy(() => import("../pages/dashboard/TicketRefund"))
);

const TicketTransacDetails = Loadable(
  lazy(() => import("../pages/dashboard/TicketTransacDetails"))
);

const SendTopUp = Loadable(lazy(() => import("../pages/dashboard/SendTopUp")));

const SendMoncash = Loadable(
  lazy(() => import("../pages/dashboard/SendMoncash"))
);

const SendNatcash = Loadable(
  lazy(() => import("../pages/dashboard/SendNatcash"))
);

const SendMoney = Loadable(lazy(() => import("../pages/dashboard/SendMoney")));

// REPORT
const Report = Loadable(
  lazy(() => import("../pages/dashboard/Report/ReportInit"))
);
const TransactionList = Loadable(
  lazy(() => import("../pages/dashboard/TransactionList"))
);

// EXCHANGE RATE
// const ExchangeRateList = Loadable(
//   lazy(() => import("../pages/dashboard/ExchangeRateList"))
// );

// FEE
// const FeeList = Loadable(lazy(() => import("../pages/dashboard/FeeList")));

// USER COMMISSION
// const UserCommissionList = Loadable(
//   lazy(() => import("../pages/dashboard/UserCommissionList"))
// );

// LIMIT TRANSACTION GENERALE
// const LimitTransactionGeneraleList = Loadable(
//   lazy(() => import("../pages/dashboard/LimitTransactionGeneraleList"))
// );

// SUPERVISOR

// AGENT
// const AgentList = Loadable(lazy(() => import("../pages/dashboard/AgentList")));

// const AgentCreate = Loadable(
//   lazy(() => import("../pages/dashboard/AgentCreate"))
// );
// CUSTOMER

// USER
const UserAccount = Loadable(
  lazy(() => import("../pages/dashboard/UserAccount"))
);
// MAIN
// const PolitiqueConf = Loadable(lazy(() => import("../pages/PolitiqueConf")));
// const TermesConditions = Loadable(
//   lazy(() => import("../pages/TermesConditions"))
// );
// const PolitiqueSecurite = Loadable(
//   lazy(() => import("../pages/PolitiqueSecurite"))
// );
// const ComingSoon = Loadable(lazy(() => import("../pages/ComingSoon")));
// const Maintenance = Loadable(lazy(() => import("../pages/Maintenance")));
// const Page500 = Loadable(lazy(() => import("../pages/Page500")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));
